/**
 * Color Contrast Utilities
 *
 * Determines optimal text color (black or white) for a background using the
 * WCAG 2.0 relative-luminance formula. Mirrors the gamma-corrected
 * `getContrastTextColor` used in the host repos (storage-theme-payload /
 * gli-payload-multitenant); kept self-contained here because unit-table is a
 * standalone published package and cannot import from them.
 */

// Normalize to a 6-digit hex (expand #abc shorthand); returns null if not valid hex.
function normalizeHex(color: string): string | null {
  const hex = color.trim().replace(/^#/, "");
  const expanded = hex.length === 3 ? hex.split("").map((c) => c + c).join("") : hex;
  return /^[0-9a-fA-F]{6}$/.test(expanded) ? expanded : null;
}

/**
 * Relative luminance of a hex color per the WCAG formula (gamma-decoded sRGB).
 * @returns luminance in [0, 1], or null when the input isn't a valid hex color.
 */
export function getLuminance(color: string): number | null {
  const hex = normalizeHex(color);
  if (!hex) return null;

  const toLinear = (channel: number) =>
    channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);

  const r = toLinear(parseInt(hex.substring(0, 2), 16) / 255);
  const g = toLinear(parseInt(hex.substring(2, 4), 16) / 255);
  const b = toLinear(parseInt(hex.substring(4, 6), 16) / 255);

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Optimal text color (black or white hex) for a given background color.
 * Threshold of 0.5 matches `getContrastTextColor` across the codebase.
 * Falls back to white for unparseable input (e.g. CSS vars, malformed hex).
 */
export function getTextColorForBackground(backgroundColor: string): string {
  const luminance = getLuminance(backgroundColor);
  if (luminance === null) {
    console.warn(`getTextColorForBackground: unparseable color "${backgroundColor}", defaulting to white text`);
    return "#FFFFFF";
  }
  return luminance > 0.5 ? "#000000" : "#FFFFFF";
}
